import { createFileRoute, Link, useNavigate } from "@tanstack/solid-router";
import { createSignal, ErrorBoundary, Show, Suspense } from "solid-js";
import { RepresentationsPanel } from "~/components/representations/RepresentationsPanel";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Button, buttonVariants } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { ErrorCard } from "~/components/ui/ErrorCard";
import { useProjectQuery } from "~/lib/queries/projects";
import {
  useDeleteSourceMutation,
  useSourceQuery,
  useUpdateSourceMutation,
} from "~/lib/queries/sources";

export const Route = createFileRoute(
  "/_authenticated/projects/$projectId/sources/$sourceId/",
)({
  ssr: false,
  component: SourceDetailPage,
});

// Inline editable field component
interface InlineEditFieldProps {
  label: string;
  value: string;
  onSave: (newValue: string) => Promise<void>;
  type?: "text" | "textarea";
  placeholder?: string;
}

function InlineEditField(props: InlineEditFieldProps) {
  const [isEditing, setIsEditing] = createSignal(false);
  const [tempValue, setTempValue] = createSignal(props.value || "");
  const [isSaving, setIsSaving] = createSignal(false);

  const handleSave = async (e: Event) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await props.onSave(tempValue());
      setIsEditing(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTempValue(props.value || "");
    setIsEditing(false);
  };

  return (
    <div class="space-y-1.5 group/field">
      <span class="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
        {props.label}
      </span>
      <Show
        when={isEditing()}
        fallback={
          <>
            {/* biome-ignore lint/a11y/noStaticElementInteractions: Click to edit */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: Keyboard edit handled by clicking field */}
            <div
              onClick={() => {
                setTempValue(props.value || "");
                setIsEditing(true);
              }}
              class="min-h-10 px-3 py-2 rounded-lg border border-transparent hover:border-border hover:bg-muted/30 cursor-pointer transition-all flex items-start justify-between gap-3 group-hover/field:border-border"
            >
              <div class="text-sm text-foreground whitespace-pre-wrap break-words flex-1">
                {props.value ? (
                  props.value
                ) : (
                  <span class="text-muted-foreground/50 italic">
                    {props.placeholder || "Click to add…"}
                  </span>
                )}
              </div>
              <span class="opacity-0 group-hover/field:opacity-50 hover:!opacity-100 transition-opacity text-xs font-medium text-primary shrink-0 select-none">
                Edit
              </span>
            </div>
          </>
        }
      >
        <form onSubmit={handleSave} class="space-y-2">
          <Show
            when={props.type === "textarea"}
            fallback={
              <input
                type="text"
                value={tempValue()}
                onInput={(e) => setTempValue(e.currentTarget.value)}
                class="w-full h-10 rounded-lg border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                disabled={isSaving()}
                autocomplete="off"
                autofocus
              />
            }
          >
            <textarea
              rows="3"
              value={tempValue()}
              onInput={(e) => setTempValue(e.currentTarget.value)}
              class="w-full min-h-20 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
              disabled={isSaving()}
              autofocus
            />
          </Show>
          <div class="flex items-center gap-2">
            <button
              type="submit"
              disabled={isSaving()}
              class="inline-flex h-8 items-center px-3 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isSaving() ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving()}
              class="inline-flex h-8 items-center px-3 rounded-lg border border-border text-xs font-medium hover:bg-muted/50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </Show>
    </div>
  );
}

function SourceDetailContent() {
  const params = Route.useParams();
  const navigate = useNavigate();

  const [deleteStage, setDeleteStage] = createSignal<0 | 1 | 2>(0);
  const [confirmText, setConfirmText] = createSignal("");
  const [confirmChecked, setConfirmChecked] = createSignal(false);

  const projectQuery = useProjectQuery(() => params().projectId);
  const sourceQuery = useSourceQuery(
    () => params().projectId,
    () => params().sourceId,
  );

  const updateMutation = useUpdateSourceMutation(
    () => params().projectId,
    () => params().sourceId,
  );
  const deleteMutation = useDeleteSourceMutation(() => params().projectId);

  const projectName = () => projectQuery.data?.name || "Project";
  const sourceCitation = () => sourceQuery.data?.citation || "Source Detail";

  const handleUpdateField = async (
    field: "citation" | "description" | "comments" | "contextDate",
    newValue: string,
  ) => {
    await updateMutation.mutateAsync({
      [field]: newValue.trim(),
    });
  };

  const isStage1Valid = () =>
    confirmText().trim() === "DELETE" && confirmChecked();

  const handleContinueToDelete = (e: Event) => {
    e.preventDefault();
    if (isStage1Valid()) {
      setDeleteStage(2);
    }
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ sourceId: params().sourceId });
    setDeleteStage(0);
    navigate({
      to: "/projects/$projectId/sources",
      params: { projectId: params().projectId },
      replace: true,
    });
  };

  return (
    <div class="space-y-6 max-w-4xl">
      {/* Breadcrumb */}
      <div class="flex items-center justify-between gap-4">
        <Breadcrumb
          items={[
            { label: projectName(), to: `/projects/${params().projectId}` },
            {
              label: "Sources",
              to: `/projects/${params().projectId}/sources`,
            },
            { label: sourceCitation() },
          ]}
        />
        <Link
          to="/projects/$projectId/sources/$sourceId/workbench"
          params={{
            projectId: params().projectId,
            sourceId: params().sourceId,
          }}
          class={buttonVariants({ variant: "default", size: "sm" })}
        >
          Open workbench
        </Link>
      </div>

      <Show
        when={sourceQuery.isSuccess}
        fallback={<div>Loading source details...</div>}
      >
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Info (Left columns) */}
          <div class="md:col-span-2 space-y-6">
            <div class="rounded-xl border border-border bg-card p-6 space-y-5">
              <h2 class="text-sm font-bold text-foreground border-b border-border pb-2 uppercase tracking-wider">
                Source Information
              </h2>

              <InlineEditField
                label="Citation"
                value={sourceQuery.data?.citation || ""}
                placeholder="Smith Family Bible, births registry, p. 12"
                onSave={(val) => handleUpdateField("citation", val)}
              />

              <InlineEditField
                label="Description"
                value={sourceQuery.data?.description || ""}
                placeholder="A brief description of this source…"
                type="textarea"
                onSave={(val) => handleUpdateField("description", val)}
              />

              <InlineEditField
                label="Comments"
                value={sourceQuery.data?.comments || ""}
                placeholder="Additional researcher notes, log details…"
                type="textarea"
                onSave={(val) => handleUpdateField("comments", val)}
              />

              <InlineEditField
                label="Context Date"
                value={sourceQuery.data?.contextDate || ""}
                placeholder="e.g., circa 1850, 1910–1920"
                onSave={(val) => handleUpdateField("contextDate", val)}
              />
            </div>

            <RepresentationsPanel
              projectId={params().projectId}
              sourceId={params().sourceId}
            />
          </div>

          {/* Sidebar Actions (Right column) */}
          <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-4 space-y-3">
              <h3 class="text-xs font-bold text-foreground uppercase tracking-wider">
                Actions
              </h3>
              <Button
                variant="destructive"
                class="w-full justify-center"
                onClick={() => {
                  setConfirmText("");
                  setConfirmChecked(false);
                  setDeleteStage(1);
                }}
              >
                Delete Source
              </Button>
            </div>

            {/* Source details / Metadata */}
            <div class="rounded-xl border border-border bg-card p-4 text-xs space-y-2">
              <h3 class="text-xs font-bold text-foreground uppercase tracking-wider mb-3">
                Metadata
              </h3>
              <div class="flex justify-between py-1 border-b border-border/40">
                <span class="text-muted-foreground font-medium">Source ID</span>
                <span class="font-mono text-muted-foreground/80">
                  {params().sourceId.substring(0, 8)}...
                </span>
              </div>
              <div class="flex justify-between py-1">
                <span class="text-muted-foreground font-medium">
                  Created At
                </span>
                <span class="text-muted-foreground/80">
                  {sourceQuery.data?.createdAt
                    ? new Date(
                        Number(sourceQuery.data.createdAt.seconds) * 1000,
                      ).toLocaleDateString()
                    : "Unknown"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Show>

      {/* Two-Stage Deletion Dialog */}
      <Dialog
        open={deleteStage() > 0}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteStage(0);
          }
        }}
      >
        <DialogContent class="border-destructive/30 max-w-md">
          {deleteStage() === 1 ? (
            <>
              <DialogHeader>
                <DialogTitle class="text-destructive flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="size-5"
                  >
                    <title>Warning</title>
                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  Delete Source (Stage 1)
                </DialogTitle>
                <DialogDescription class="pt-2">
                  You are about to delete this source. This will permanently
                  remove the source and all associated representations, files,
                  and excerpts.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleContinueToDelete} class="space-y-4 py-2">
                <div class="space-y-2">
                  <label
                    for="confirm-delete-detail"
                    class="text-xs font-medium text-foreground block"
                  >
                    Type <span class="font-bold text-destructive">DELETE</span>{" "}
                    to confirm:
                  </label>
                  <input
                    id="confirm-delete-detail"
                    type="text"
                    value={confirmText()}
                    onInput={(e) => setConfirmText(e.currentTarget.value)}
                    placeholder="DELETE"
                    class="w-full h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    autocomplete="off"
                  />
                </div>

                <label class="flex items-start gap-2.5 text-xs text-muted-foreground select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmChecked()}
                    onChange={(e) => setConfirmChecked(e.currentTarget.checked)}
                    class="mt-0.5 rounded border-muted focus-visible:ring-ring"
                  />
                  <span>
                    I understand that this will delete everything related to
                    this source.
                  </span>
                </label>

                <DialogFooter class="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDeleteStage(0)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={!isStage1Valid()}
                  >
                    Continue
                  </Button>
                </DialogFooter>
              </form>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle class="text-destructive flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="size-5"
                  >
                    <title>Confirm Delete</title>
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  Confirm Permanent Deletion (Stage 2)
                </DialogTitle>
                <DialogDescription class="pt-2">
                  Are you absolutely sure? This action cannot be undone. All
                  files, transcriptions, and excerpts associated with this
                  source will be gone forever.
                </DialogDescription>
              </DialogHeader>

              {deleteMutation.isError && (
                <p class="text-xs text-destructive">
                  {deleteMutation.error instanceof Error
                    ? deleteMutation.error.message
                    : "Failed to delete source. Please try again."}
                </p>
              )}

              <DialogFooter class="pt-4">
                <Button
                  variant="outline"
                  onClick={() => setDeleteStage(1)}
                  disabled={deleteMutation.isPending}
                >
                  Back
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? "Deleting..." : "Delete forever"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SourceDetailPage() {
  return (
    <ErrorBoundary
      fallback={(err) => (
        <ErrorCard
          title="Source error"
          message={err?.message ?? "An unexpected error occurred."}
        />
      )}
    >
      <Suspense fallback={<div>Loading source details...</div>}>
        <SourceDetailContent />
      </Suspense>
    </ErrorBoundary>
  );
}
